import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { Encyclopedia, EncyclopediaEntry } from '../lib/types';
import { Bar, EmptyState, ErrorState, LoadingGrid, Modal, Panel, TypeChip } from '../components/ui';
import { formatDate, formatNumber, titleCase } from '../lib/format';

/**
 * The Trainer Encyclopedia.
 *
 * You cannot browse anybody you have not met. The Living Trainers deliberately
 * have no directory — a cast list turns them into a checklist to work through
 * rather than people you run into — so this page shows entries you have earned
 * and a count of how many are still out there, and nothing else.
 *
 * That count is the collectible part. "9 of 47" is a goal; a grid of forty-seven
 * greyed portraits with names under them would be a spoiler and a chore.
 */

export function EncyclopediaPage() {
  const state = useAsync<Encyclopedia>(() => api.get('/statistics/encyclopedia'), []);
  const [open, setOpen] = useState<EncyclopediaEntry | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <LoadingGrid count={6} height={150} />;

  const { entries, met, total, ratio } = state.data;

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Trainer Encyclopedia</h1>
          <div className="page-head__sub">
            {met} of {total} trainers met
          </div>
        </div>
      </div>

      <Panel raised={false}>
        <div className="stack-sm">
          <Bar ratio={ratio} label={`${Math.round(ratio * 100)}% known`} tall />
          <p className="tiny muted" style={{ margin: 0 }}>
            Entries are written when a trainer does something for you — feeds one of your
            Pokémon, warms an egg, stops by your Grove. There is no list of who is left,
            because meeting them is supposed to be a surprise.
          </p>
        </div>
      </Panel>

      {entries.length === 0 ? (
        <EmptyState art="☺" title="You haven't met anyone yet">
          Put some Pokémon out in a Grove and get an egg incubating. The Living Trainers
          come to you.
        </EmptyState>
      ) : (
        <div className="encyclopedia">
          {entries.map((entry) => (
            <button
              key={entry.npcKey}
              type="button"
              className="npc-card pxframe"
              onClick={() => setOpen(entry)}
            >
              <span className="npc-card__portrait">
                {entry.portrait ? (
                  <img src={entry.portrait} alt="" className="pixel" width={56} height={56} />
                ) : (
                  <span className="npc-card__silhouette" aria-hidden />
                )}
              </span>
              <span className="npc-card__body">
                <strong>{entry.username}</strong>
                <span className="tiny muted">
                  {entry.trainerClass} · {titleCase(entry.region.toLowerCase())}
                </span>
                <span className="tiny muted">
                  {formatNumber(entry.interactionCount)}{' '}
                  {entry.interactionCount === 1 ? 'meeting' : 'meetings'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {open && <EntryModal entry={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function EntryModal({ entry, onClose }: { entry: EncyclopediaEntry; onClose: () => void }) {
  return (
    <Modal onClose={onClose} labelledBy="npc-title" wide>
      <div className="stack">
        <header className="npc-entry__head">
          {entry.portrait && (
            <img src={entry.portrait} alt="" className="pixel" width={72} height={72} />
          )}
          <div>
            <h2 id="npc-title" style={{ margin: 0 }}>
              {entry.username}
            </h2>
            <div className="tiny muted">
              {entry.trainerClass} · {titleCase(entry.region.toLowerCase())} · Generation{' '}
              {entry.generation}
            </div>
          </div>
        </header>

        <blockquote className="npc-entry__quote">“{entry.quote}”</blockquote>

        {entry.biography && <p style={{ margin: 0 }}>{entry.biography}</p>}
        <p className="tiny muted" style={{ margin: 0 }}>
          Around the world, {entry.username} {entry.habitatFlavor}.
        </p>

        {entry.favoriteTypes.length > 0 && (
          <div className="row row--wrap">
            <span className="tiny muted">Favours</span>
            {entry.favoriteTypes.map((type) => (
              <TypeChip key={type} type={type} />
            ))}
          </div>
        )}

        {entry.signatureTeam.length > 0 && (
          <div className="stack-sm">
            <h3 className="section-head" style={{ margin: 0 }}>
              Signature team
            </h3>
            <div className="npc-entry__team">
              {entry.signatureTeam.map((mon) => (
                <Link key={mon.id} to={`/dex/${mon.id}`} className="npc-entry__mon pxwell">
                  <img src={mon.sprite} alt="" className="pixel" width={48} height={48} />
                  <span className="tiny">{mon.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {entry.badges.length > 0 && (
          <p className="tiny muted" style={{ margin: 0 }}>
            Known for the {entry.badges.join(', ')}.
          </p>
        )}

        <div className="pxwell stack-sm" style={{ padding: 'var(--gap-sm)' }}>
          <div className="tiny muted">Your history together</div>
          <div className="row row--wrap">
            <span className="chip">First met {formatDate(entry.firstMetAt)}</span>
            <span className="chip">Last seen {formatDate(entry.lastMetAt)}</span>
            {Object.entries(entry.interactions).map(([kind, count]) => (
              <span key={kind} className="chip">
                {titleCase(kind.toLowerCase())} × {formatNumber(count)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
